<script lang="ts" setup>
import { ElNotification } from 'element-plus'
import type { PropType } from 'vue'
import type { UserRoleType } from '~/services/apiClient'
import { useUsersStore } from '~/stores/users'
import UserRoleSelector from '~/components/display/UserRoleSelector.vue'
import { useUserStore } from '~/stores/user'
import EmailAddressesInput from '~/components/inputs/EmailAddressesInput.vue'

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  userLabel: { type: String, default: 'User' },
  role: { type: String as PropType<UserRoleType> },
  extraMessages: { type: Array as PropType<string[]> },
})
const emit = defineEmits<{ (e: 'update:modelValue', value: number[]): void }>()
const addUsersDialogOpen = useVModel(props, 'modelValue', emit)
const usersStore = useUsersStore()
const userStore = useUserStore()
const emailAddresses = ref<string[]>([])
const selectedRole = ref<UserRoleType | undefined>(props.role)

function addUsers() {
  usersStore.inviteUsers(emailAddresses.value, selectedRole.value).then(() => {
    ElNotification({
      title: `Invited ${emailAddresses.value.length} users`,
      type: 'success',
    })
    usersStore.load()
    emailAddresses.value = []
    addUsersDialogOpen.value = false
  })
}
</script>

<template>
  <el-dialog
    v-model="addUsersDialogOpen"
    :title="`Invite ${userLabel}s`"
    center
  >
    <p>
      Enter an email for each {{ userLabel.toLowerCase() }} you would like to
      invite.
    </p>

    <EmailAddressesInput
      v-model="emailAddresses"
      :extra-messages="extraMessages"
    />

    <div
      v-if="!props.role && userStore.hasManagementPermissions"
      class="mt-4 mb-2 flex items-center gap-4"
    >
      <UserRoleSelector
        v-model="selectedRole"
        :required="false"
        :allow-manager="true"
        placeholder="Select Role (optional)"
        style="max-width: 49.5%"
      />
      <Help title="About User Roles">
        <p>
          Only managers can manage other users and organization settings. Other
          roles have the same permissions but help organize users by their
          function.
        </p>
        <p>
          Users cannot select the manager role during signup unless specifically
          invited as a manager.
        </p>
      </Help>
    </div>
    <template #footer>
      <div class="flex">
        <el-button
          size="large"
          class="flex-1"
          @click="addUsersDialogOpen = false"
        >
          Cancel
        </el-button>
        <el-button
          type="primary"
          size="large"
          :disabled="emailAddresses.length === 0"
          class="flex-1"
          @click="addUsers"
        >
          Invite
          {{ emailAddresses.length }} {{ userLabel.toLowerCase() }}s
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>
